// ============================================================================
// EnterpriseFormModal — canonical create/edit/copy modal shell
// ============================================================================
// Centralises EVERYTHING that the four modal-bug shapes need to attack:
//
//   1. Modal container markup — wrapper writes it; per-screen code never
//      touches it. No way to inject style={{ position: 'relative' }} on the
//      container ever again.
//
//   2. Modal header text — wrapper derives it from { mode, noun } as a
//      fixed string ('Add New <noun>' / 'Edit <noun>' / 'Copy <noun>').
//      Per-screen code can't add (N), record code, or form data to the
//      visible header. Period.
//
//   3. Minimized-strip title — wrapper sets up useMinimizableForm with a
//      canonical buildTitle that DOES include (N) for Adds and record code
//      for edit/copy. Per-screen code only customises optional `getStripCode`
//      and `getStripFormLabel` callbacks; they can't shortcut their way into
//      the modal header.
//
//   4. Slot-reuse Add counter — the counter lives in the SCREEN (the only
//      place that can call isMinimized to find a free slot). Wrapper consumes
//      `createInstanceId` and passes it through to useMinimizableForm.
//
//   5. Loading overlay anchor — wrapper renders the overlay inside its own
//      modal-container (which uses CSS `position: fixed` — a positioned
//      value, so `position: absolute; inset: 0` on the overlay anchors fine
//      without needing any inline override on the container).
//
//   6. ConfirmDialog portal — wrapper renders the discard + save confirms.
//      Per-screen onSave is called only AFTER the user confirms; if onSave
//      throws, wrapper maps 409 / 400-duplicate to user-friendly toasts.
//
// Per-screen FormModal becomes a thin shell that supplies a noun, the
// populate/validate/save callbacks, and the field-set JSX via render prop.
// Typical instance: 80-100 lines instead of the previous 300+.
//
// See docs/ENTERPRISE_SCREEN_CHECKLIST.md §4.5 ("Two titles, two different
// rules") for the philosophy; this file is the implementation that makes
// it physically impossible to violate.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { apiErrorCode } from 'utils/errorHandling';
import { useStandardConfirms } from './useStandardConfirms';
import { useMinimizableForm } from './useMinimizableForm';
import type { OnMinimizedArgs } from './useMinimizableForm';
import { ConsequencePreview, consequenceMessage, type Consequence } from './ConsequencePreview';

// ============================================================================
// Public API
// ============================================================================

export type FormModalMode = 'create' | 'edit' | 'copy';
export type FormModalSize = 'sm' | 'md' | 'lg';

/** Shape passed to the children render prop. */
export interface EnterpriseFormModalChildArgs<TForm, TRecord> {
  form: TForm;
  setForm: React.Dispatch<React.SetStateAction<TForm>>;
  mode: FormModalMode;
  record: TRecord | null;
  isSaving: boolean;
  /** Returns the validation error for `field`, or undefined if none. */
  getFieldError: (field: string) => string | undefined;
  /** Clears the validation error for `field` (call on input change). */
  clearFieldError: (field: string) => void;
  /** Direct setter for full validation state — escape hatch when callers
   *  need to set multiple errors from inside `validate`. */
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export interface EnterpriseFormModalProps<TForm extends object, TRecord = unknown> {
  // ─── Visibility ─────────────────────────────────────────────────────────
  isOpen: boolean;
  mode: FormModalMode;
  /** The record being edited/copied; null for create. */
  record: TRecord | null;

  // ─── Header text (fixed-per-mode, derived by wrapper) ───────────────────
  /** Singular noun used to derive header text — "Customer" → "Add New
   *  Customer" / "Edit Customer" / "Copy Customer". MUST NOT include (N) or
   *  record code; those go in the minimized-strip title only. */
  noun: string;
  /** Optional verb override for create mode header. Default: "Add New". */
  createVerb?: string;
  /** Optional verb override for copy mode header. Default: "Copy". */
  copyVerb?: string;
  /** Optional verb override for edit mode header. Default: "Edit". For an edit
   *  that is really one specific action (e.g. "Stop" an effective-dated row). */
  editVerb?: string;

  // ─── Form lifecycle ─────────────────────────────────────────────────────
  emptyForm: TForm;
  /** Build the initial form state when the modal opens fresh.
   *  Called on each `isOpen` transition false→true. */
  populateOnOpen: (mode: FormModalMode, record: TRecord | null) => TForm;
  /** Optional client-side validation. Return `{}` for valid, or
   *  `{ fieldName: 'error message' }` for invalid. The wrapper sets
   *  fieldErrors from the return value and aborts save. */
  validate?: (form: TForm, mode: FormModalMode) => Record<string, string>;
  /** Perform the actual API save. Throw on error; the wrapper catches
   *  the standard cases (409 → conflict, 400+already → duplicate, else
   *  the response message or "Save failed"). */
  onSave: (form: TForm, mode: FormModalMode, record: TRecord | null) => Promise<void>;

  // ─── Confirmation text + success toast (all optional) ────────────────────
  /** Confirm-dialog title shown before save. Default: "Confirm <verb>". */
  confirmSaveTitle?: (mode: FormModalMode, form: TForm) => string;
  /** Confirm-dialog body shown before save. Default: short auto-generated. */
  confirmSaveMessage?: (mode: FormModalMode, form: TForm) => string;
  /** Label for the confirm-dialog action button. Default: "Add" / "Update" / "Duplicate". */
  confirmSaveLabel?: (mode: FormModalMode) => string;
  /** Success-toast text after save. Default: short auto-generated. */
  successMessage?: (mode: FormModalMode, form: TForm) => string;

  // ─── Consequence ("What will happen") ───────────────────────────────────
  /** Outcome of saving, derived from the current form values; null = nothing
   *  to state. Shown under the fields as a ConsequencePreview, used as the
   *  save-confirmation text (instead of confirmSaveMessage), and a 'danger'
   *  tone turns the save button and the confirm dialog red. Required on any
   *  date-based or hard-to-undo action (ENTERPRISE_SCREEN_CHECKLIST §4.10). */
  consequence?: (form: TForm, mode: FormModalMode) => Consequence | null;

  // ─── Size ───────────────────────────────────────────────────────────────
  /** Default 'md'. */
  size?: FormModalSize;

  // ─── Minimize-to-bar wiring ─────────────────────────────────────────────
  tabId?: string;
  moduleName: string;
  /** From the screen's slot-reuse counter — the (N) for Add in the strip. */
  createInstanceId?: string;
  /** Key for edit/copy modal id + strip title. Typically `record?.code`. */
  recordKey?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  /** For Add: short label derived from form data — used in the strip title
   *  ("Add - {label} (N)"). Default: tries form.code, form.name. */
  getStripFormLabel?: (form: TForm) => string | undefined;
  /** For edit/copy: short label derived from record + form — used in the
   *  strip title ("Edit - {label}"). Default: recordKey. */
  getStripRecordLabel?: (record: TRecord | null, form: TForm) => string | undefined;

  // ─── Callbacks ──────────────────────────────────────────────────────────
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;

  // ─── Field set ──────────────────────────────────────────────────────────
  children: (args: EnterpriseFormModalChildArgs<TForm, TRecord>) => React.ReactNode;
}

// ============================================================================
// Implementation
// ============================================================================

function defaultCreateVerb() { return 'Add New'; }
function defaultCopyVerb()   { return 'Copy'; }

function defaultConfirmSaveLabel(mode: FormModalMode): string {
  if (mode === 'edit') return 'Update';
  if (mode === 'copy') return 'Duplicate';
  return 'Add';
}

function defaultConfirmSaveTitle(mode: FormModalMode): string {
  if (mode === 'edit') return 'Confirm Update';
  if (mode === 'copy') return 'Confirm Duplicate';
  return 'Confirm Save';
}

function pickFormLabel(form: Record<string, unknown>): string | undefined {
  for (const k of ['code', 'name', 'title', 'attributeKey', 'userName', 'roleCode', 'displayName']) {
    const v = form[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

export function EnterpriseFormModal<TForm extends object, TRecord = unknown>(
  props: EnterpriseFormModalProps<TForm, TRecord>,
): React.ReactElement | null {
  const {
    isOpen, mode, record,
    noun, createVerb, copyVerb, editVerb,
    emptyForm, populateOnOpen, validate, onSave,
    confirmSaveTitle, confirmSaveMessage, confirmSaveLabel, successMessage,
    consequence: getConsequence,
    size = 'md',
    tabId, moduleName, createInstanceId, recordKey, minimizeRef,
    getStripFormLabel, getStripRecordLabel,
    onClose, onSaved, onRestoreModal,
    children,
  } = props;

  // ─── State owned by the wrapper ──────────────────────────────────────────
  const [form, setForm] = useState<TForm>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { confirmDiscard, confirmCustom, ConfirmDialogPortal } = useStandardConfirms();

  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors],
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // ─── Field-error reset on open ────────────────────────────────────────────
  // This effect MUST NOT populate the form. `useMinimizableForm` owns the
  // open-transition populate (see its header: "Callers must NOT add their own
  // useEffect([open]) that populates form") and deliberately SKIPS it on a
  // restore so the parked form data survives.
  //
  // This wrapper previously called `setForm(populateOnOpen(mode, record))`
  // here, which silently defeated that. Effects run in registration order, and
  // this one is registered BEFORE the hook's — so on restore it overwrote the
  // restored data with a fresh populate (the EMPTY form in create mode), and
  // the hook's guarded effect then consumed `isRestoreRef` having done nothing.
  // Every wrapper-based modal lost minimized work; the hand-rolled sample modal
  // follows the contract, which is why the sample screen masked it.
  //
  // Population now happens ONLY on an open transition, via the hook. That is
  // sufficient because every screen flow that swaps mode/record also closes and
  // reopens the modal (or minimizes and restores it) — see `handleCopy`, which
  // auto-minimizes the open modal before opening the next one.
  useEffect(() => {
    if (!isOpen) return;
    setFieldErrors({});
  }, [isOpen]);

  // ─── Minimize wiring ─────────────────────────────────────────────────────
  // populateOnOpen wrapped to match useMinimizableForm's no-arg shape. The hook
  // stores this in an always-current ref, so a fresh open populates with the
  // mode/record in effect at that moment.
  const minimizePopulate = useCallback((): TForm => populateOnOpen(mode, record), [mode, record, populateOnOpen]);

  const { handleMinimize, isDirty, resetBaseline } = useMinimizableForm<TForm>({
    open:        isOpen,
    mode,
    form,
    setForm,
    emptyForm,
    tabId,
    moduleName,
    createInstanceId,
    recordKey,
    populateOnOpen: minimizePopulate,
    // Canonical strip-title builder. Authors customise only the label
    // derivation; the FORMAT — "Add - {label} (N)" / "Edit - {label}" — is
    // wrapper-controlled.
    buildTitle: useCallback(({ mode: m, form: f, createInstanceId: id, recordKey: key }) => {
      if (m === 'create') {
        const idx = id || '1';
        const label = (getStripFormLabel ? getStripFormLabel(f) : pickFormLabel(f as unknown as Record<string, unknown>));
        return label ? `Add - ${label} (${idx})` : `Add ${noun} (${idx})`;
      }
      const label = getStripRecordLabel ? getStripRecordLabel(record, f) : (key || pickFormLabel(f as unknown as Record<string, unknown>));
      if (m === 'copy') return `Copy - ${label || noun}`;
      return `Edit - ${label || noun}`;
    }, [noun, getStripFormLabel, getStripRecordLabel, record]),
    onMinimized: useCallback((args: OnMinimizedArgs) => {
      onRestoreModal(args.mode, args.createInstanceId, args.recordKey);
    }, [onRestoreModal]),
    onClose,
  });

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // ─── Cancel (dirty-check + discard confirm) ──────────────────────────────
  const handleCancel = useCallback(async () => {
    if (!isDirty) { onClose(); return; }
    const ok = await confirmDiscard();
    if (ok) { setForm(emptyForm); onClose(); }
  }, [isDirty, confirmDiscard, onClose, emptyForm]);

  // ─── Save (validate → confirm → onSave → reset → close) ──────────────────
  const handleSave = useCallback(async () => {
    // Client validation
    const errors = validate ? validate(form, mode) : {};
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      notificationService.toast.warning('Please fix the highlighted fields.');
      // Scroll the first errored field into view if it has a [data-field] hook
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input, textarea, select')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});

    // Confirm — a consequence, when the screen states one, IS the confirmation text.
    const outcome = getConsequence?.(form, mode) ?? null;
    const ok = await confirmCustom({
      title:        (confirmSaveTitle?.(mode, form) ?? defaultConfirmSaveTitle(mode)),
      message:      outcome
        ? consequenceMessage(outcome)
        : (confirmSaveMessage?.(mode, form) ?? `${defaultConfirmSaveLabel(mode)} this ${noun.toLowerCase()}?`),
      confirmLabel: (confirmSaveLabel?.(mode)        ?? defaultConfirmSaveLabel(mode)),
      variant:      outcome?.tone === 'danger' ? 'danger' : 'default',
    });
    if (!ok) return;

    // Save
    setIsSaving(true);
    try {
      await onSave(form, mode, record);
      notificationService.toast.success(
        successMessage?.(mode, form)
        ?? `${noun} ${mode === 'edit' ? 'updated' : mode === 'copy' ? 'duplicated' : 'added'}.`,
      );
      resetBaseline(emptyForm);
      setForm(emptyForm);
      onSaved();
    } catch (err: unknown) {
      // Standard error mapping. Caller-thrown messages take precedence.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const code = apiErrorCode(e);
      if (code === 'DUPLICATE') {
        notificationService.toast.error(e?.response?.data?.message ?? 'This record already exists.');
      } else if (e?.response?.status === 409) {
        notificationService.toast.error('This row was modified by another user. Please refresh and try again.');
      } else if (e?.response?.status === 400 && /already|duplicate|exists/i.test(e?.response?.data?.message || '')) {
        // Back ends without a code-first envelope: message-text fallback.
        notificationService.toast.error(e?.response?.data?.message ?? 'This record already exists.');
      } else {
        notificationService.toast.error(e?.response?.data?.message || e?.message || 'Save failed.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [form, mode, record, noun, validate, onSave, confirmCustom, confirmSaveTitle, confirmSaveMessage, confirmSaveLabel, successMessage, getConsequence, resetBaseline, emptyForm, onSaved]);

  if (!isOpen) return null;

  // ─── Derived modal header text — FIXED per mode + noun ───────────────────
  const verbCreate = createVerb ?? defaultCreateVerb();
  const verbCopy   = copyVerb   ?? defaultCopyVerb();
  const verbEdit   = editVerb   ?? 'Edit';
  const titleText =
    mode === 'edit' ? `${verbEdit} ${noun}` :
    mode === 'copy' ? `${verbCopy} ${noun}` :
                      `${verbCreate} ${noun}`;

  const sizeClass = size === 'sm' ? 'modal-sm' : size === 'lg' ? 'modal-lg' : 'modal-md';
  const outcome = getConsequence?.(form, mode) ?? null;

  return (
    <>
      <div className="modal-backdrop" />
      {/* NOTE: never add inline style={{ position: 'relative' }} here. The
          CSS rule .modal-container is `position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%)`. The loading overlay's
          `position: absolute; inset: 0` anchors fine because fixed is a
          positioned value. See ENTERPRISE_SCREEN_CHECKLIST §4.5. */}
      <div className={`modal-container ${sizeClass}`}>
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{titleText}</h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
                type="button"
              >
                <IconSystem.chevronDown size={18} />
              </button>
            )}
            <button className="modal-close" onClick={handleCancel} disabled={isSaving} type="button">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          {children({
            form, setForm, mode, record, isSaving,
            getFieldError, clearFieldError, setFieldErrors,
          })}
          {outcome && <ConsequencePreview consequence={outcome} />}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCancel} disabled={isSaving} type="button">
            Cancel
          </button>
          <button className={`btn ${outcome?.tone === 'danger' ? 'btn-danger' : 'btn-primary'}`} onClick={handleSave} disabled={isSaving} type="button">
            {isSaving ? 'Saving…' : (confirmSaveLabel?.(mode) ?? defaultConfirmSaveLabel(mode))}
          </button>
        </div>
      </div>

      <ConfirmDialogPortal />
    </>
  );
}

export default EnterpriseFormModal;
